import React, { useState, useEffect, useCallback } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import {
  Wallet, Loader2, Building2, Calendar, Check, X,
  TrendingUp, Trash2, RefreshCw, ChevronDown, ChevronUp,
} from "lucide-react";

const inp = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 bg-white";
const btn = "px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2";

const money = (n) => `$${Number(n || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 })}`;

export default function SettlementsManager() {
  const [tab, setTab] = useState("margin"); // margin | settlements
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    API.get("/companies", { params: { limit: 200 } })
      .then((res) => setCompanies(res.data?.data?.data ?? []))
      .catch(() => {});
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Wallet className="text-supernova-600" size={26} /> Comisiones y liquidaciones
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Margen de ganancia por pedido (comisión de la plataforma vs. ganancia de la tienda) y control de pagos a tiendas.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-100">
        {[["margin", "Margen por tienda"], ["settlements", "Liquidaciones"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition ${tab === id ? "border-supernova-500 text-supernova-700" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "margin" ? <MarginReport /> : <SettlementsTab companies={companies} />}
    </div>
  );
}

// ─── Reporte de margen por tienda ────────────────────────────────────────────
function MarginReport() {
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/settlements/margin-report", { params: { from: from || undefined, to: to || undefined } });
      setReport(res.data?.data?.report ?? []);
    } catch { toast.error("Error cargando el reporte"); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const totals = report.reduce((acc, r) => ({
    grossSubtotal: acc.grossSubtotal + r.grossSubtotal,
    commissionTotal: acc.commissionTotal + r.commissionTotal,
    netPayable: acc.netPayable + r.netPayable,
    ordersCount: acc.ordersCount + r.ordersCount,
  }), { grossSubtotal: 0, commissionTotal: 0, netPayable: 0, ordersCount: 0 });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 bg-white rounded-2xl shadow p-4">
        <div>
          <label className="text-xs font-bold text-gray-500 mb-1 block">Desde</label>
          <input type="date" className={inp} value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-500 mb-1 block">Hasta</label>
          <input type="date" className={inp} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button onClick={load} className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50`}>
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl shadow p-4">
          <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Venta bruta (productos)</p>
          <p className="text-xl font-bold text-gray-800">{money(totals.grossSubtotal)}</p>
        </div>
        <div className="bg-white rounded-2xl shadow p-4">
          <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Comisión plataforma</p>
          <p className="text-xl font-bold text-supernova-600">{money(totals.commissionTotal)}</p>
        </div>
        <div className="bg-white rounded-2xl shadow p-4">
          <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Ganancia tiendas</p>
          <p className="text-xl font-bold text-green-600">{money(totals.netPayable)}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 size={28} className="animate-spin text-supernova-500" /></div>
        ) : report.length === 0 ? (
          <div className="text-center py-14 text-gray-400">
            <TrendingUp size={36} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Sin pedidos con comisión calculada en este rango</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-semibold">
                <tr>
                  <th className="text-left px-4 py-3">Tienda</th>
                  <th className="text-left px-4 py-3">Plan</th>
                  <th className="text-right px-4 py-3">Pedidos</th>
                  <th className="text-right px-4 py-3">Venta bruta</th>
                  <th className="text-right px-4 py-3">Comisión app</th>
                  <th className="text-right px-4 py-3">Ganancia tienda</th>
                </tr>
              </thead>
              <tbody>
                {report.map((r) => (
                  <tr key={r.company.id} className="border-t border-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-800">{r.company.name}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {r.company.plan ? `${r.company.plan.name} (${Number(r.company.plan.commissionPercent)}%)` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{r.ordersCount}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{money(r.grossSubtotal)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-supernova-600">{money(r.commissionTotal)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-green-600">{money(r.netPayable)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Liquidaciones ────────────────────────────────────────────────────────────
function SettlementsTab({ companies }) {
  const [companyId, setCompanyId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [creating, setCreating] = useState(false);

  const [settlements, setSettlements] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await API.get("/settlements", { params: { status: statusFilter || undefined } });
      setSettlements(res.data?.data?.settlements ?? []);
    } catch { toast.error("Error cargando liquidaciones"); }
    finally { setLoadingList(false); }
  }, [statusFilter]);

  useEffect(() => { loadList(); }, [loadList]);

  const loadPreview = async () => {
    if (!companyId) { toast.warn("Selecciona una tienda"); return; }
    setLoadingPreview(true);
    try {
      const res = await API.get(`/settlements/pending/${companyId}`, { params: { from: from || undefined, to: to || undefined } });
      setPreview(res.data?.data?.summary ?? null);
    } catch { toast.error("Error calculando el pendiente"); }
    finally { setLoadingPreview(false); }
  };

  const handleCreate = async () => {
    if (!preview || preview.ordersCount === 0) { toast.warn("No hay pedidos pendientes para liquidar"); return; }
    setCreating(true);
    try {
      await API.post("/settlements", { companyId, from: from || undefined, to: to || undefined });
      toast.success("Liquidación creada");
      setPreview(null);
      await loadList();
    } catch (err) { toast.error(err.response?.data?.message || "Error creando la liquidación"); }
    finally { setCreating(false); }
  };

  const handleMarkPaid = async (id) => {
    const reference = window.prompt("Referencia de la transferencia (opcional):") || undefined;
    try {
      await API.post(`/settlements/${id}/pay`, { reference });
      toast.success("Liquidación marcada como pagada");
      await loadList();
    } catch (err) { toast.error(err.response?.data?.message || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Borrar esta liquidación? Los pedidos vuelven a quedar pendientes de liquidar.")) return;
    try {
      await API.delete(`/settlements/${id}`);
      toast.success("Liquidación eliminada");
      await loadList();
    } catch (err) { toast.error(err.response?.data?.message || "Error"); }
  };

  const toggleExpand = async (s) => {
    if (expanded === s.id) { setExpanded(null); return; }
    setExpanded(s.id);
    try {
      const res = await API.get(`/settlements/${s.id}`);
      setDetail(res.data?.data?.settlement ?? null);
    } catch { toast.error("Error cargando el detalle"); }
  };

  return (
    <div className="space-y-6">
      {/* Crear nueva liquidación */}
      <div className="bg-white rounded-2xl shadow p-5">
        <h3 className="text-base font-bold text-gray-700 mb-3">Nueva liquidación</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <label className="text-xs font-bold text-gray-500 mb-1 block">Tienda *</label>
            <select className={inp} value={companyId} onChange={(e) => { setCompanyId(e.target.value); setPreview(null); }}>
              <option value="">Selecciona una tienda</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Desde</label>
            <input type="date" className={inp} value={from} onChange={(e) => { setFrom(e.target.value); setPreview(null); }} />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 mb-1 block">Hasta</label>
            <input type="date" className={inp} value={to} onChange={(e) => { setTo(e.target.value); setPreview(null); }} />
          </div>
          <button onClick={loadPreview} disabled={loadingPreview} className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-60`}>
            {loadingPreview ? <Loader2 size={14} className="animate-spin" /> : <Calendar size={14} />} Calcular pendiente
          </button>
        </div>

        {preview && (
          <div className="mt-4 border border-gray-100 rounded-xl p-4 bg-gray-50">
            {preview.ordersCount === 0 ? (
              <p className="text-sm text-gray-400">No hay pedidos entregados pendientes de liquidar en ese rango.</p>
            ) : (
              <>
                <div className="grid sm:grid-cols-4 gap-3 text-sm">
                  <div><p className="text-gray-400 text-xs">Pedidos</p><p className="font-bold text-gray-800">{preview.ordersCount}</p></div>
                  <div><p className="text-gray-400 text-xs">Venta bruta</p><p className="font-bold text-gray-800">{money(preview.grossSubtotal)}</p></div>
                  <div><p className="text-gray-400 text-xs">Comisión app</p><p className="font-bold text-supernova-600">{money(preview.commissionTotal)}</p></div>
                  <div><p className="text-gray-400 text-xs">A pagar a la tienda</p><p className="font-bold text-green-600">{money(preview.netPayable)}</p></div>
                </div>
                <button onClick={handleCreate} disabled={creating} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-60 mt-4`}>
                  <Check size={14} /> {creating ? "Creando…" : "Crear liquidación"}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Lista de liquidaciones */}
      <div className="bg-white rounded-2xl shadow p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-gray-700">Historial</h3>
          <select className={`${inp} w-auto`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Todas</option>
            <option value="pendiente">Pendientes</option>
            <option value="pagada">Pagadas</option>
          </select>
        </div>

        {loadingList ? (
          <div className="flex justify-center py-10"><Loader2 size={28} className="animate-spin text-supernova-500" /></div>
        ) : settlements.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">Sin liquidaciones todavía</p>
        ) : (
          <div className="space-y-2">
            {settlements.map((s) => (
              <div key={s.id} className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3">
                  <Building2 size={16} className="text-gray-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-800 text-sm truncate">{s.company?.name}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(s.periodStart).toLocaleDateString("es-CO")} – {new Date(s.periodEnd).toLocaleDateString("es-CO")} · {s.ordersCount} pedido(s)
                    </p>
                  </div>
                  <p className="text-sm font-bold text-green-600 shrink-0">{money(s.netPayable)}</p>
                  <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold ${s.status === "pagada" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    {s.status === "pagada" ? "Pagada" : "Pendiente"}
                  </span>
                  <button onClick={() => toggleExpand(s)} className="text-gray-400 hover:text-gray-700 shrink-0">
                    {expanded === s.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {s.status === "pendiente" && (
                    <>
                      <button onClick={() => handleMarkPaid(s.id)} className={`${btn} text-xs bg-green-50 text-green-700 hover:bg-green-100 shrink-0`}>
                        <Check size={11} /> Marcar pagada
                      </button>
                      <button onClick={() => handleDelete(s.id)} className={`${btn} text-xs bg-red-50 text-red-500 hover:bg-red-100 shrink-0`}>
                        <Trash2 size={11} />
                      </button>
                    </>
                  )}
                </div>
                {expanded === s.id && detail && (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                    {s.reference && <p className="text-xs text-gray-500 mb-2">Referencia: {s.reference}</p>}
                    <table className="w-full text-xs">
                      <thead className="text-gray-400">
                        <tr><th className="text-left py-1">Pedido</th><th className="text-right py-1">Subtotal</th><th className="text-right py-1">Comisión</th><th className="text-right py-1">Neto tienda</th></tr>
                      </thead>
                      <tbody>
                        {detail.orders.map((o) => (
                          <tr key={o.id} className="border-t border-gray-100">
                            <td className="py-1">{o.orderNumber}</td>
                            <td className="py-1 text-right">{money(o.subtotal)}</td>
                            <td className="py-1 text-right">{money(o.commissionAmount)}</td>
                            <td className="py-1 text-right">{money(o.companyNetAmount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
